/**
 * The vocabulary of the new registration form, mirrored from the backend.
 *
 * `GENDERS`, `PERSON_DOCUMENT_KINDS` and the adult age are all defined server-side
 * (`backend/src/graphql/utils/gender.ts` and `.../personDocuments.ts`) and validated there —
 * these are the display labels for the same codes. **Codes are stored, labels are for display**:
 * renaming a code orphans every row already written with it.
 */

export const GENDER_OPTIONS = [
    { value: 'MALE', label: 'Male' },
    { value: 'FEMALE', label: 'Female' },
    { value: 'TRANSGENDER', label: 'Transgender' },
    { value: 'NOT_STATED', label: 'Prefer not to say' },
];

export const PERSON_DOCUMENT_KIND_OPTIONS = [
    { key: 'THUMBPRINT_SIGNATURE', value: 'THUMBPRINT_SIGNATURE', text: 'Thumbprint signature' },
    { key: 'PERMISSION_SLIP', value: 'PERMISSION_SLIP', text: 'Parental permission slip' },
    { key: 'REGISTRATION_FORM', value: 'REGISTRATION_FORM', text: 'Registration form' },
    { key: 'OTHER', value: 'OTHER', text: 'Other' },
];

/** Mirrors `PERSON_DOCUMENT_CONTENT_TYPES` — the server re-checks, so keep the two in step. */
export const PERSON_DOCUMENT_ACCEPT = 'image/jpeg,image/png,application/pdf';

/** The age at which someone can give consent for themselves. */
export const ADULT_AGE = 18;

/**
 * Whole years old, from a bare `yyyy-MM-dd` string or a `Date` picked in the form.
 *
 * A date of birth is a calendar date, never an instant: a `Date` is read through its *local*
 * accessors and a string through its first ten characters, so neither path can shift the day
 * into the previous one in a browser west of UTC. The day either side of a birthday is what
 * decides whether a guardian may consent for themselves, so this is not cosmetic.
 */
export const ageInYears = (dob: Date | string | null | undefined, asOf: Date = new Date()): number | null => {
    if (dob == null || dob === '') return null;

    let year: number;
    let month: number;
    let day: number;

    if (dob instanceof Date) {
        if (Number.isNaN(dob.getTime())) return null;
        year = dob.getFullYear();
        month = dob.getMonth() + 1;
        day = dob.getDate();
    } else {
        const match = String(dob).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (match == null) return null;
        [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
    }

    let age = asOf.getFullYear() - year;

    if (asOf.getMonth() + 1 < month || (asOf.getMonth() + 1 === month && asOf.getDate() < day)) {
        age -= 1;
    }

    return age;
};

/**
 * Under 18 — and `false` when the date of birth is unknown.
 *
 * Deliberately not "unknown means treat as a minor": the guardian section stays greyed until a
 * date of birth says otherwise, rather than demanding parental details from everyone whose age
 * has not been recorded yet.
 */
export const isMinor = (dob: Date | string | null | undefined, asOf: Date = new Date()): boolean => {
    const age = ageInYears(dob, asOf);
    return age != null && age < ADULT_AGE;
};

/** The health-information box's limit. Mirrors `HEALTH_INFORMATION_MAX_LENGTH` on the server. */
export const HEALTH_INFORMATION_MAX_LENGTH = 1000;

/**
 * Everything the registration form needs before it will save, as a draft of its answers.
 *
 * **Enforced in the form only, never on the server** (owner, 2026-11-03). The classic "Add person"
 * form shares `adminCreateUser` and sends none of this; it has to keep saving exactly as it does.
 */
export interface RegistrationDraft {
    name: string;
    /** `YYYY-MM-DD`, or '' when not chosen. */
    dob: string;
    /** From `chooseCodingCommunity` — whether a community is chosen and the identifier settled. */
    coding: 'none' | 'chooseHome' | 'ok';
    phone: string;
    /** Answers the phone question for someone who has none, rather than blocking them. */
    noPhone: boolean;
    emergencyContactName: string;
    emergencyContactPhone: string;
    consentDataCollection: boolean;
    healthSafetyBriefed: boolean;
    consentHealthSafety: boolean;
    isMinor: boolean;
    guardianConsentsToChildData: boolean;
}

/**
 * What is still missing, in the order the form asks for it — empty when it may save.
 *
 * A list rather than a boolean, because a disabled Save button with no reason is how a centre
 * manager ends up scrolling the whole form looking for the one box they missed.
 *
 * Address is deliberately **not** here: it was required and phone was not, which is the wrong way
 * round for reaching someone. ID sighted is not here either — it records what happened, and "not
 * yet" is a real answer.
 */
export const missingForRegistration = (d: RegistrationDraft): string[] => {
    const missing: string[] = [];
    const blank = (s: string) => s.trim() === '';

    if (blank(d.name)) missing.push('Name');
    if (blank(d.dob)) missing.push('Date of birth');
    if (d.coding === 'none') missing.push('A community');
    if (d.coding === 'chooseHome') missing.push('Which location is their home');
    if (blank(d.phone) && !d.noPhone) missing.push('Phone, or tick "No phone"');
    if (blank(d.emergencyContactName)) missing.push('Emergency contact name');
    if (blank(d.emergencyContactPhone)) missing.push('Emergency contact number');
    if (!d.consentDataCollection) missing.push('Consent to the collection and use of their information');
    if (!d.healthSafetyBriefed) missing.push('Briefed on health and safety');
    if (!d.consentHealthSafety) missing.push('Consent to the health and safety risks');
    if (d.isMinor && !d.guardianConsentsToChildData) missing.push("Parent or guardian's consent to the child's data");

    return missing;
};
